import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { ICorporate, ICorporateInput } from "models/corporate";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  addCorporate,
  corporateCells,
  deleteCorporate,
  editCorporate,
  getCorporates,
} from "framework/corporates";
import AddCorporate from "./partials/AddCorporate/AddCorporate";
import EditCorporate from "./partials/EditCorporate/EditCorporate";
import DeleteCorporate from "./partials/DeleteCorporate/DeleteCorporate";
import { handleError } from "helpers/utils";
import { IWorkshop } from "models/workshop";
import { getWorkshops } from "framework/workshops";
import { useTranslation } from "react-i18next";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import useFetchSection from "hooks/useFetchSection";
import { addInvitationForGroupWorkshops } from "framework/workshopInvitations";
import { ICompany, IUser } from "models/company";
import { getCompanies } from "framework/company";
import Search from "components/Search/Search";
import { getCorporateUsers, getUsers } from "framework/users";
interface Props {
  feature: Feature;
}

const Corporates: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const [loading3, setLoading3] = useState<boolean>(false);
  const { t, i18n } = useTranslation();

  const [FilterCorporates, setFilterCorporates] = useState<
    ICorporate[]
  >([] as ICorporate[]);
  const [Companies, setCompanies] = useState<ICompany[]>([] as ICompany[]);
  const [Users, setUsers] = useState<IUser[]>([] as IUser[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCorporate, setSelectedCorporate] =
    useState<ICorporate>({} as ICorporate);
  const [inputObj, setInputObj] = useState<ICorporateInput>(
    {} as ICorporateInput
  );
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addCorporateMutation = useMutation({
    mutationFn: (createInput: ICorporateInput) => {
      return addCorporate(createInput);
    },
  });

  const editCorporateMutation = useMutation({
    mutationFn: (data: { createInput: ICorporateInput; id: string }) => {
      return editCorporate(data.createInput, selectedCorporate.id);
    },
  });

  const deleteCorporateMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteCorporate(code);
    },
  });

  // get Vendor data
  const fetchCompanies = useCallback(async () => {
    setLoading3(true);
    try {
      let response: any = await queryClient.fetchQuery("get Companies", () =>
        getCompanies()
      );
      setCompanies(response);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchUsers = useCallback(async () => {
    setLoading3(true);
    try {
      let response: any = await queryClient.fetchQuery("get Users", () =>
        getCorporateUsers()
      );
      setUsers(response);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  // get Venue data

  useEffect(() => {
    fetchCompanies();
    fetchUsers();
    // eslint-disable-next-line
  }, []);

  const {
    loading,
    data: Corporates,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCorporates(),
    fetchPermission: () => getPermission(47),
    section: "Workshops Groups",
    onSuccess: (data) => setFilterCorporates(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name === "companies") {
      let companies = value.map((company: any) => company.value).join(";");
      setInputObj((prev) => ({
        ...prev,
        [name]: companies,
      }));
    }
    else if (name === "users") {
      let users = value.map((user: any) => user.value).join(";");
      setInputObj((prev) => ({
        ...prev,
        [name]: users,
      }));
    }
    else {
      setInputObj((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Corporate: ICorporate) => {
    setSelectedCorporate(Corporate);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCorporate({} as ICorporate);
  };

  const handleOpenDelete = (Corporate: ICorporate) => {
    setSelectedCorporate(Corporate);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCorporate({} as ICorporate);
  };

  const getBodyCells = (row: ICorporate) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.name}</Typography>
            </TableCell>
          ),
        };
      if (key === "companies")
        return {
          id: "companies",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.companies}</Typography>
            </TableCell>
          ),
        };

      if (key === "users")
        return {
          id: "users",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.users}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "createdOn")
        return {
          id: "createdOn",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.createdOn.toString()}
              </Typography>
            </TableCell>
          ),
        };



      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title="Edit">
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };

  const handleAdd = async () => {
    if (inputObj.companies.length < 2)
      return setAlert({
        open: true,
        message: "Choose at least 2 companies",
        type: "error",
      });
    if (inputObj.users.length < 1)
      return setAlert({
        open: true,
        message: "Choose at least 1 user",
        type: "error",
      });
    try {
      setLoading2(true);
      await addCorporateMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Corporate added successfully",
        type: "success",
      });
      setInputObj({} as ICorporateInput);
      revalidate();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    if (!inputObj.companies && !inputObj.users && !inputObj.name)
      return setAlert({
        open: true,
        message: "No changes made",
        type: "error",
      });
    try {
      setLoading2(true);
      let createInput: ICorporateInput = {
        companies:
          inputObj.companies ||
          selectedCorporate.companiesIds,
        users:
          inputObj.users ||
          selectedCorporate.usersIds,
        name: inputObj.name || selectedCorporate.name
      };
      await editCorporateMutation.mutateAsync({
        createInput,
        id: selectedCorporate.id,
      });
      setAlert({
        open: true,
        message: "Corporate updated successfully",
        type: "success",
      });
      setInputObj({} as ICorporateInput);
      revalidate();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCorporateMutation.mutateAsync(selectedCorporate.id);
      setAlert({
        open: true,
        message: "Corporate deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCorporates(Corporates!);
    let filtered = Corporates!.filter((cor) => cor.name.toLowerCase().includes(value.toLowerCase()));
    setFilterCorporates(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title="Add Workshop">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterCorporates.length > 0 && (
          <List
            headCells={corporateCells}
            data={FilterCorporates}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCorporates || FilterCorporates.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon />}
              header="No Corporates"
              message="There are no corporates available"
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCorporate
          open={openAdd}
          loading={loading2}
          companies={Companies}
          users={Users}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditCorporate
          open={openEdit}
          loading={loading2}
          companies={Companies}
          users={Users}
          corporate={selectedCorporate}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteCorporate
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Corporates;
